import { CircularProgress, styled } from '@mui/material';

import { useMemo } from 'react';

import { I18nResources, useI18n } from '@app-velocity/i18n';
import {
  AIIcon,
  ErrorIcon,
  PauseIcon,
  WarningCircleIcon,
} from '@app-velocity/icons';
import { SimpleStatus } from '@app-velocity/common';

export type StatusBadgeProps = {
  status: SimpleStatus | null;
};

const StyledBadge = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: 13,
  gap: theme.spacing(1),
  borderRadius: theme.spacing(1),
  padding: 0,
  background: 'transparent',
  color: '#fff',
  border: 'none',
  whiteSpace: 'nowrap',
  svg: {
    fill: '#fff',
  },
}));

const IconContainer = styled('div')({
  width: 16,
  height: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  svg: {
    width: '100%',
    height: '100%;',
  },
});

export const StatusBadge = ({ status }: StatusBadgeProps) => {
  const { t } = useI18n();

  const statusText = useMemo(() => {
    switch (status) {
      case 'success':
        return t('shared:badge.successful');
      case 'error':
        return t('shared:badge.unsuccessful');
      case 'warning':
        return t('shared:badge.unsuccessful');
      case 'paused':
        return t('shared:badge.paused');
      default:
        return t('shared:badge.in_progress');
    }
  }, [status, t]) as I18nResources;

  const icon = useMemo(() => {
    switch (status) {
      case 'success':
        return <AIIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningCircleIcon />;
      case 'paused':
        return <PauseIcon />;
      default:
        return <CircularProgress size={12} color="inherit" />;
    }
  }, [status]);

  return (
    <StyledBadge>
      {statusText} <IconContainer>{icon}</IconContainer>
    </StyledBadge>
  );
};
